import React, { useState } from 'react';
import axios from 'axios';
import { FaRegTrashAlt } from 'react-icons/fa';
import { getCookie } from './auth/cookies';
import { toast } from 'react-toastify';
import FileExplorerModal from './fileexplorer';
import ReactMarkdown from 'react-markdown';
import { IoIosFolderOpen } from "react-icons/io";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import customRenderers from './customMD';

function SubmissionsPage({ submissions, showDelete, setSubmissions }) {
    const [showExplorer, setShowExplorer] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [expandedSubmissions, setExpandedSubmissions] = useState({});

    // Handle opening file explorer with the selected files
    const openFileExplorer = (files) => {
        setSelectedFiles(files);
        setShowExplorer(true);
    };

    // Handle closing the file explorer
    const closeFileExplorer = () => {
        setShowExplorer(false);
    };

    // Handle deletion of a submission
    const handleDelete = (submission_id) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this submission?");
        if (!confirmDelete) return;
    
        axios.post('/api/delete_submission/', 
            { submission_id },
            { headers: { 'X-CSRFToken': getCookie('csrftoken') } }
        )
        .then(() => {
            toast.success('Submission deleted successfully!');
            if (setSubmissions){
                setSubmissions(submissions.filter(submission => submission.id !== submission_id));
            }
        })
        .catch((err) => {
            toast.error(`Error deleting submission: ${err.message}`);
        });
    };

    // Toggle expanded state of a submission
    const toggleSubmission = (submissionId) => {
        setExpandedSubmissions(prev => ({
            ...prev,
            [submissionId]: !prev[submissionId]
        }));
    };

    

    // Function to preprocess markdown and group content under headings
    const preprocessMarkdownForSections = (markdown) => {
        const lines = markdown.split('\n');
        const sections = [];
        let currentSection = null;
        let beforeFirstHeading = [];

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);

            if (headingMatch) {
                const level = headingMatch[1].length;
                const title = headingMatch[2];

                // Save content before first heading
                if (currentSection === null && beforeFirstHeading.length > 0) {
                    sections.push({
                        type: 'content',
                        content: beforeFirstHeading.join('\n')
                    });
                }

                // Save previous section
                if (currentSection) {
                    sections.push(currentSection);
                }

                // Start new section
                currentSection = {
                    type: 'heading',
                    level: level,
                    title: title,
                    content: []
                };
            } else {
                if (currentSection === null) {
                    beforeFirstHeading.push(line);
                } else {
                    currentSection.content.push(line);
                }
            }
        }

        // Add the last section
        if (currentSection) {
            sections.push(currentSection);
        }

        return sections;
    };

    // Enhanced renderContent function
    const renderContent = (description) => {
        const sections = preprocessMarkdownForSections(description);

        return (
            <div>
                <style jsx>{`
                    details > summary {
                        list-style: none;
                    }
                    details > summary::-webkit-details-marker {
                        display: none;
                    }
                    details[open] > summary .expandable-arrow {
                        transform: rotate(90deg);
                    }
                `}</style>
                {sections.map((section, index) => {
                    if (section.type === 'content') {
                        // Regular content (not under a heading)
                        const content = section.content;
                        if (content.includes("<img")) {
                            const parts = content.split(/(<img.*?>)/g);
                            return (
                                <div key={index}>
                                    {parts.map((part, partIndex) => {
                                        if (part.startsWith("<img")) {
                                            return <div key={partIndex} dangerouslySetInnerHTML={{ __html: part }} className="my-4 text-center" />;
                                        } else if (part.trim()) {
                                            return <ReactMarkdown key={partIndex} components={customRenderers}>{part}</ReactMarkdown>;
                                        }
                                        return null;
                                    })}
                                </div>
                            );
                        } else {
                            return <ReactMarkdown key={index} components={customRenderers}>{content}</ReactMarkdown>;
                        }
                    } else if (section.type === 'heading') {
                        // Heading with content
                        const contentMarkdown = section.content.join('\n');
                        const isExpandable = section.level === 2 || section.level === 3;

                        if (isExpandable) {
                            // Render as expandable section
                            const sectionClass = section.level === 2 
                                ? "my-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800"
                                : "my-4 border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden bg-white dark:bg-gray-800";
                            
                            const summaryClass = section.level === 2
                                ? "bg-gray-50 dark:bg-gray-700 px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors font-semibold text-lg flex items-center select-none"
                                : "bg-gray-100 dark:bg-gray-700 px-3 py-2 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium flex items-center select-none";
                            
                            const contentClass = section.level === 2 
                                ? "px-4 py-3 bg-white dark:bg-gray-800"
                                : "px-3 py-2 bg-white dark:bg-gray-800";

                            return (
                                <details key={index} className={sectionClass}>
                                    <summary className={summaryClass}>
                                        <span className="expandable-arrow mr-3 transition-transform duration-200">
                                            ▶
                                        </span>
                                        <span className="text-gray-900 dark:text-gray-100">{section.title}</span>
                                    </summary>
                                    <div className={contentClass}>
                                        {contentMarkdown.includes("<img") ? (
                                            (() => {
                                                const parts = contentMarkdown.split(/(<img.*?>)/g);
                                                return parts.map((part, partIndex) => {
                                                    if (part.startsWith("<img")) {
                                                        return <div key={partIndex} dangerouslySetInnerHTML={{ __html: part }} className="my-4 text-center" />;
                                                    } else if (part.trim()) {
                                                        return <ReactMarkdown key={partIndex} components={{...customRenderers, h2: ({children}) => <h4 className="text-lg font-semibold mt-4 mb-2 text-gray-900 dark:text-gray-100">{children}</h4>, h3: ({children}) => <h5 className="text-base font-medium mt-3 mb-2 text-gray-900 dark:text-gray-100">{children}</h5>}}>{part}</ReactMarkdown>;
                                                    }
                                                    return null;
                                                });
                                            })()
                                        ) : (
                                            <ReactMarkdown components={{...customRenderers, h2: ({children}) => <h4 className="text-lg font-semibold mt-4 mb-2 text-gray-900 dark:text-gray-100">{children}</h4>, h3: ({children}) => <h5 className="text-base font-medium mt-3 mb-2 text-gray-900 dark:text-gray-100">{children}</h5>}}>{contentMarkdown}</ReactMarkdown>
                                        )}
                                    </div>
                                </details>
                            );
                        } else {
                            // Regular heading (h1, h4, h5, h6)
                            const headingMarkdown = `${'#'.repeat(section.level)} ${section.title}\n\n${contentMarkdown}`;
                            if (headingMarkdown.includes("<img")) {
                                const parts = headingMarkdown.split(/(<img.*?>)/g);
                                return (
                                    <div key={index}>
                                        {parts.map((part, partIndex) => {
                                            if (part.startsWith("<img")) {
                                                return <div key={partIndex} dangerouslySetInnerHTML={{ __html: part }} className="my-4 text-center" />;
                                            } else if (part.trim()) {
                                                return <ReactMarkdown key={partIndex} components={customRenderers}>{part}</ReactMarkdown>;
                                            }
                                            return null;
                                        })}
                                    </div>
                                );
                            } else {
                                return <ReactMarkdown key={index} components={customRenderers}>{headingMarkdown}</ReactMarkdown>;
                            }
                        }
                    }
                    return null;
                })}
            </div>
        );
    };

    return (
        <section className="mb-12">
            <h2 className="text-2xl text-gray-700 dark:text-tertiary-light mb-4">Submissions</h2>
            <div className="overflow-x-auto">
                <table className="w-full border border-gray-300 dark:border-gray-600">
                    <thead>
                        <tr>
                            <th className="p-4 border-b"></th> {/* Expand/Collapse column */}
                            {showDelete && <th className='p-4 border-b'></th>}
                            <th className="p-4 border-b">Score</th>
                            <th className="p-4 border-b">Final_score</th>
                            <th className="p-4 border-b">Verified</th>
                            <th className="p-4 border-b">Files</th>
                        </tr>
                    </thead>
                    <tbody>
                        {submissions.map((submission, index) => (
                            <React.Fragment key={index}>
                                <tr className={`text-center ${expandedSubmissions[submission.id] ? 'bg-gray-100 dark:bg-gray-800' : ''}`}>
                                    <td className="p-4 border-b">
                                        <button 
                                            onClick={() => toggleSubmission(submission.id)}
                                            className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                        >
                                            {expandedSubmissions[submission.id] ? 
                                                <IoChevronUp className="text-xl" /> : 
                                                <IoChevronDown className="text-xl" />
                                            }
                                        </button>
                                    </td>
                                    {showDelete && (
                                        <td className="p-4 border-b">
                                            <button className="text-red-500" onClick={() => handleDelete(submission.id)}>
                                                <FaRegTrashAlt />
                                            </button>
                                        </td>
                                    )}
                                    <td className="p-4 border-b">{submission.score}</td>
                                    <td className="p-4 border-b">{submission.final_score}</td>
                                    <td className="p-4 border-b">{submission.verify ? 'Yes' : 'No'}</td>
                                    <td className="p-4 border-b">
                                        <button
                                            onClick={() => openFileExplorer(submission.files)}
                                            className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-2xl"
                                        >
                                            <IoIosFolderOpen />
                                        </button>
                                    </td>
                                </tr>
                                {expandedSubmissions[submission.id] && (
                                    <tr>
                                        <td colSpan={showDelete ? 5 : 4} className="p-6 border-b bg-white dark:bg-gray-900">
                                            <div className="text-left">
                                                <h3 className="text-lg font-medium mb-2">Description</h3>
                                                <div className="prose dark:prose-invert prose-img:my-2 prose-headings:my-4 max-w-none">
                                                    {renderContent(submission.description)}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* File Explorer Modal */}
            {showExplorer && (
                <FileExplorerModal files={selectedFiles} onClose={closeFileExplorer} />
            )}
        </section>
    );
}

export default SubmissionsPage;